import { useContext, useRef } from "react";
import { Field, useForm } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { getIn } from "final-form";
import { v1 as uuidv1 } from "uuid";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import styles from "./FinancialDocsCard.module.scss";
import MeritoButton from "../../../components/common/MeritoButtons/MeritoButtons";
import FinancialFileItem from "./FinancialFileItem";
import API from "../../../rest/api";
import { checkPDFProtection } from "../components/utils/functions";
import {
  UnderwritingSteps,
  pdfProtectionStates,
  uploadStates,
} from "../../../interfaces/interfaces";
import {
  UnderwritingFormContext,
  UnderwritingFormContextType,
} from "../context/UnderwritingFormContext";

/** A `{ title, action }` pair, from classification or from the form itself. */
export type DocsCardError = {
  title: string;
  action: string;
};

type Props = {
  /** Card heading, e.g. "FY 2025-26" or "MIS (Aug 2025 - Jul 2026)". */
  title: string;
  /** Pill beside the title. Omitted on the MIS card, which has no document type. */
  badgeText?: string | null;
  description: string;
  uploadBtnText: string;
  /** Array field holding the docs, e.g. `revenueDetails[0].auditedFinancials` or `misReports`. */
  filesFieldName: string;
  /** Written to underwriting_upload_jobs.section, so jobs stay attributable per card. */
  section: string;
  /** Per-doc classification entries, for the file rows' tags and warnings. */
  classificationResult?: any[] | null;
  /** The card-level `{ error_state }`, reported when every doc failed. */
  classificationErrorDetails?: any;
  /** True while this card's classification job is queued or running. */
  isProcessing?: boolean;
  /** Shown instead of the classification error while nothing is uploaded. */
  missingDocsError?: DocsCardError | null;
  newUploadedDocIds: number[];
  setNewUploadedDocIds: React.Dispatch<React.SetStateAction<number[]>>;
  deleteDocIds: number[];
  setDeleteDocIds: React.Dispatch<React.SetStateAction<number[]>>;
  /** When given, the button runs this instead of opening the file picker. */
  onUploadClick?: () => void;
};

/**
 * One upload card: heading, optional badge, description, upload button, error block
 * and the list of uploaded files. Shared by each financial year and by MIS, which
 * the design draws identically apart from their copy.
 */
const FinancialDocsCard = ({
  title,
  badgeText = null,
  description,
  uploadBtnText,
  filesFieldName,
  section,
  classificationResult = null,
  classificationErrorDetails = null,
  isProcessing = false,
  missingDocsError = null,
  newUploadedDocIds,
  setNewUploadedDocIds,
  deleteDocIds,
  setDeleteDocIds,
  onUploadClick,
}: Props) => {
  const { getState, change } = useForm();
  const { handleUnderwritingFileJobs, sendFileJobToApi } = useContext(
    UnderwritingFormContext
  ) as UnderwritingFormContextType;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadFileToS3 = async (
    currentFieldName: string,
    fileData: File,
    fileFormJob: any
  ) => {
    try {
      const formData = new FormData();
      formData.append("documentType", "financials");
      formData.append("file", fileData);
      const data = await API.post("/app/uploadDocs", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      }).then((res) => res.data);

      const isPdfProtected = await checkPDFProtection(fileData);
      let jobDetails = null;

      if (fileData.type === "application/pdf") {
        if (isPdfProtected === pdfProtectionStates.passwordNotRequired) {
          jobDetails = await sendFileJobToApi(
            UnderwritingSteps.revenue,
            currentFieldName,
            {
              s3FileKey: data?.Key,
              fileMetaData: {
                fileName: fileData?.name,
                fileType: fileData?.type,
                requiredPassword: false,
                password: null,
              },
              status: uploadStates.doneUploading,
            }
          );
        } else if (isPdfProtected === pdfProtectionStates.passwordRequired) {
          jobDetails = await sendFileJobToApi(
            UnderwritingSteps.revenue,
            currentFieldName,
            {
              s3FileKey: data?.Key,
              fileMetaData: {
                fileName: fileData?.name,
                fileType: fileData?.type,
                requiredPassword: true,
                password: null,
              },
              status: uploadStates.requiredPassword,
            }
          );
        }
      } else {
        jobDetails = await sendFileJobToApi(
          UnderwritingSteps.revenue,
          currentFieldName,
          {
            s3FileKey: data?.Key,
            fileMetaData: {
              fileName: fileData?.name,
              fileType: fileData?.type,
              requiredPassword: false,
              password: null,
            },
            status: uploadStates.doneUploading,
          }
        );
      }

      setNewUploadedDocIds((prev) => [...prev, jobDetails?.docId]);
      change(`${currentFieldName}`, {
        ...fileFormJob,
        uploadedFile: {
          fileKey: data?.Key,
          fileName: fileData?.name,
          fileType: fileData?.type,
          docId: jobDetails?.id,
        },
      });
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles: File[] = Array.from(event.target.files || []);

    const existingFiles = getIn(getState().values, filesFieldName) || [];
    const updatedFiles = [...existingFiles];

    newFiles.forEach((file) => {
      const fileFormObj = { selectedFile: file, uploadedFile: null };
      updatedFiles.push(fileFormObj);
      const currentFieldName = `${filesFieldName}[${updatedFiles.length - 1}]`;
      const fileJob = {
        jobId: uuidv1(),
        step: UnderwritingSteps.revenue,
        section,
        formFieldName: currentFieldName,
        s3FileKey: null,
        fileMetaData: {
          fileName: file.name,
          fileType: file.type,
          requiredPassword: false,
          password: null,
        },
        status: uploadStates.uploading,
      };
      change(currentFieldName, fileFormObj);
      handleUnderwritingFileJobs(
        UnderwritingSteps.revenue,
        currentFieldName,
        fileJob
      );
      uploadFileToS3(currentFieldName, file, fileFormObj);
    });

    // Reset input so same file can be re-uploaded if needed
    event.target.value = "";
  };

  const errorState = classificationErrorDetails?.error_state;
  const hasClassificationError =
    !isProcessing && !!errorState && Object.keys(errorState).length > 0;

  return (
    <div className={styles.docsCard}>
      <div className={styles.cardHeader}>
        <div className={styles.cardTitleRow}>
          <div className={styles.cardTitle}>{title}</div>
          {badgeText ? (
            <div className={styles.cardBadge}>{badgeText}</div>
          ) : null}
        </div>
        <div className={styles.cardDescription}>{description}</div>
      </div>

      <div className={styles.uploadBtnContainer}>
        {!onUploadClick && (
          <input
            type="file"
            multiple
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: "none" }}
            accept=".pdf,.xls,.xlsx"
          />
        )}
        <MeritoButton
          type="secondary-2"
          btnText={uploadBtnText}
          size="md"
          disabled={isProcessing}
          onClick={() => {
            onUploadClick ? onUploadClick() : fileInputRef.current?.click();
          }}
        />
        {isProcessing && (
          <div className={styles.processingText}>
            Processing your files. Please wait...
          </div>
        )}
      </div>

      <Field
        name={filesFieldName}
        subscription={{ value: true, error: true, touched: true }}
      >
        {({ input, meta }) => {
          const files = Array.isArray(input.value) ? input.value : [];
          const hasUploadedDocs = files.some(
            (f: { uploadedFile?: { docId?: number } }) =>
              !!f?.uploadedFile?.docId
          );
          // Nothing uploaded yet is the form's own error to report; once docs are
          // there, the only thing worth saying comes back from classification.
          const showMissingDocsError =
            !hasUploadedDocs && !!missingDocsError && !!meta?.error && !!meta?.touched;
          const showClassificationError =
            hasUploadedDocs && hasClassificationError;
          if (!showMissingDocsError && !showClassificationError) return null;
          const error = showMissingDocsError
            ? (missingDocsError as DocsCardError)
            : { title: errorState.title, action: errorState.action };
          return (
            <div className={styles.errorMainContainer}>
              <div className={styles.errorContainer}>
                <div className={styles.errorTitle}>
                  <ErrorOutlineIcon className={styles.errorTitleIcon} />
                  <div className={styles.errorTitleText}>{error.title}</div>
                </div>
                <div className={styles.errorAction}>{error.action}</div>
              </div>
            </div>
          );
        }}
      </Field>

      <FieldArray name={filesFieldName}>
        {({ fields }) =>
          fields.length ? (
            <div className={styles.uploadedFilesList}>
              {fields.map((innerFieldName, index) => (
                <FinancialFileItem
                  key={innerFieldName}
                  outerFieldName={filesFieldName}
                  fieldName={innerFieldName}
                  stepType={UnderwritingSteps.revenue}
                  newUploadedDocIds={newUploadedDocIds}
                  setNewUploadedDocIds={setNewUploadedDocIds}
                  deleteDocIds={deleteDocIds}
                  setDeleteDocIds={setDeleteDocIds}
                  fieldIndex={index}
                  fyClassificationResult={classificationResult as any}
                  fyClassificationErrorDetails={classificationErrorDetails}
                  isJobProcessing={isProcessing}
                />
              ))}
            </div>
          ) : null
        }
      </FieldArray>
    </div>
  );
};

export default FinancialDocsCard;
